import { useT } from "@/i18n";
import { framesChartMessages } from "@/i18n/messages/frames-chart";
import { cn } from "@/lib/cn";
import { ChartEndLabels } from "./chart-parts";
import { formatChartValue, type ChartDrawingProps } from "./chart-points";

/** The drawing's inset from its top and bottom edge, in % of its height. */
const INSET = 8;

/** Each point's place in the 100 × 100 drawing, highest value on top. */
function linePlaces(values: readonly number[]): Array<[number, number]> {
  let low = Math.min(...values);
  let high = Math.max(...values);
  if (low === high) {
    low -= 1;
    high += 1;
  }
  const last = Math.max(values.length - 1, 1);
  return values.map((value, index) => [
    values.length === 1 ? 50 : (index / last) * 100,
    INSET + (1 - (value - low) / (high - low)) * (100 - 2 * INSET),
  ]);
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * Points as a line with a soft area under it, in the accent, the shown point
 * marked. Opened, every point is a button. Inline SVG stretched to its box
 * (strokes keep their width); the dots are HTML so they stay round.
 */
export function ChartLine({
  title,
  unit,
  points,
  shown,
  selected,
  onSelect,
  opened,
  testIdPrefix,
}: ChartDrawingProps) {
  const t = useT(framesChartMessages);
  const places = linePlaces(points.map((p) => p.value));
  const line = places.map(([x, y]) => `${round(x)},${round(y)}`).join(" ");
  const first = places[0]!;
  const end = places[places.length - 1]!;
  const area = `${round(first[0])},100 ${line} ${round(end[0])},100`;
  return (
    <>
      <div
        className="relative mt-auto min-h-[4em] flex-1 pt-[0.9em]"
        role={opened ? "group" : undefined}
        aria-label={
          opened ? t("lineTrend", { title: title || t("chart") }) : undefined
        }
        aria-hidden={opened ? undefined : "true"}
        data-testid={`${testIdPrefix}-line`}
      >
        <div className="relative h-full w-full">
          <svg
            className="absolute inset-0 h-full w-full overflow-visible"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <polygon
              points={area}
              fill="color-mix(in srgb, var(--color-accent) 18%, transparent)"
            />
            <polyline
              points={line}
              fill="none"
              stroke="var(--color-accent)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {places.map(([x, y], index) => {
            const style = { left: `${x}%`, top: `${y}%` };
            const dot = (
              <span
                className={cn(
                  "block h-[0.55em] w-[0.55em] rounded-full",
                  index === shown
                    ? "bg-accent"
                    : "bg-[color-mix(in_srgb,var(--color-accent)_45%,transparent)]",
                )}
              />
            );
            const p = points[index]!;
            return opened ? (
              <button
                key={`${p.label}-${index}`}
                type="button"
                className="nodrag absolute flex h-[1.6em] w-[1.6em] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full"
                style={style}
                aria-label={t("pointValue", {
                  label: p.label,
                  value: `${formatChartValue(p.value)}${unit ? ` ${unit}` : ""}`,
                })}
                aria-pressed={index === shown}
                onClick={() => onSelect(index === selected ? null : index)}
                data-testid={`${testIdPrefix}-point-${index}`}
              >
                {dot}
              </button>
            ) : (
              index === shown && (
                <span
                  key={index}
                  className="absolute -translate-x-1/2 -translate-y-1/2"
                  style={style}
                >
                  {dot}
                </span>
              )
            );
          })}
        </div>
      </div>
      <ChartEndLabels
        first={points[0]?.label ?? ""}
        last={points[points.length - 1]?.label ?? ""}
        className={opened ? "pt-[0.3em]" : undefined}
        testId={`${testIdPrefix}-labels`}
      />
    </>
  );
}
